import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ExportTypeEnum } from '@models/common/export-type.enum';
import { TextMessageRecord } from "@models/text-messages-templates/text-message-record";
import { TextMessageStatus } from "@models/text-messages-templates/text-message-status.enum";
import {
  OutgoingSmsMessagesExportService
} from "@pages/outgoing-messages/services/outgoing-sms-messages-export.service";
import { OutgoingSmsMessagesGridService } from "@pages/outgoing-messages/services/outgoing-sms-messages-grid.service";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import {
  GridServerSideComponent
} from "@shared/components/grid/components/grid-server-side/grid-server-side.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { IPartialHeaderData } from "@shared/services/header.service";
import { MenuService } from "@shared/services/menu.service";
import { PageSizeService } from "@shared/services/page-size.service";
import { TextMessageApiService } from "@shared/services/sms-templates.service";
import { convertEnumToFilterOption } from "@shared/utils/enum-to-filter-option.helper";
import { excelReportTitleStyles } from "@shared/utils/excel-title-for-report";
import { CellClickedEvent, ExcelExportParams, ExcelStyle, GetRowIdParams, GridApi } from 'ag-grid-community';
import { GridOptions } from "ag-grid-enterprise";
import { ToastrService } from "ngx-toastr";
import { ModalFormsService } from "../../../../forms/services/modal-forms.service";
import {
  AgOutgoingMessagesActionsComponent
} from "../outgoing-email-messages-page/components/ag-ougoing-emails-actions/ag-outgoing-messages-actions.component";
import {
  OutgoingTextMessageDetailsComponent
} from "./components/outgoing-text-message-details/outgoing-text-message-details.component";

@Component({
  selector: 'app-outgoing-sms-messages',
  standalone: true,
  imports: [
    ButtonComponent,
    OutgoingTextMessageDetailsComponent,
    GridServerSideComponent
  ],
  providers: [
    TextMessageApiService,
    OutgoingSmsMessagesGridService,
    OutgoingSmsMessagesExportService
  ],
  templateUrl: 'outgoing-sms-messages-page.component.html',
  styleUrl: '../outgoing-email-messages-page/outgoing-email-messages-page.component.scss',
})
export class OutgoingSmsMessagesPageComponent extends CommonPageComponent implements OnInit {
  private modalFormsService = inject(ModalFormsService);
  private textMessageApiService = inject(TextMessageApiService);
  private outgoingSmsMessagesGridService = inject(OutgoingSmsMessagesGridService);
  private outgoingSmsMessagesExportService = inject(OutgoingSmsMessagesExportService);
  private toastr = inject(ToastrService);
  private menuService = inject(MenuService);
  private destroyRef = inject(DestroyRef);

  override readonly headerData: IPartialHeaderData = {
    title: 'Outgoing SMS Messages',
    iconClass: 'ic-outgoing-emails-20'
  };

  currentTextMessageRecordIndex = -1;
  currentTextMessageItemNumber = 0;
  checkedTextMessageRecords: TextMessageRecord[] = [];
  textMessagesRows: TextMessageRecord[] = [];
  isDetailsLoading = false;
  $selectedTextMessageRecord = signal<TextMessageRecord | null>(null);
  modalOpened = this.modalFormsService.modalOpened;

  override ngOnInit(): void {
    super.ngOnInit();
    this.listenGridDataLoaded();
    this.setGridConfig();
    this.menuService.setMenuItemOpenState('Outgoing Messages');
  }

  listenGridDataLoaded(): void {
    this.outgoingSmsMessagesGridService.dataLoaded$.asObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.textMessagesRows = result.records;

        if (this.currentTextMessageRecordIndex >= 0) {
          this.updateSelectedTextMessage();
        }
      });
  }

  // =====  Actions  =====

  cellClickHandler(params: CellClickedEvent<TextMessageRecord>) {
    this.openTextMessageRecordPreview({
      record: params.data as TextMessageRecord,
      index: params.rowIndex as number
    });
  }

  resendTextMessage(textMessageRecord: TextMessageRecord): void {
    this.textMessageApiService.resendOutgoingTextMessage(textMessageRecord.textMessageId)
      .subscribe({
        next: () => this.reloadGrid(),
        error: () => this.toastr.error('Something happened while sending text', 'Error')
      });
  }

  deleteTextMessage(textMessageRecord: TextMessageRecord): void {
    if (confirm('Deleting the text message will not cancel pending eSigning envelopes or recall sent text messages. To cancel an eSigning request, you must void the envelope. Do you want to continue?')) {
      this.textMessageApiService.deleteOutgoingTextMessage(textMessageRecord.textMessageId)
        .subscribe({
          next: () => this.reloadGrid(),
          error: () => this.toastr.error('Something went wrong with deleting!', 'Error'),
        });
    }
  }

  // =====  Handle text message details  =====

  openTextMessageRecordPreview(data: { record: TextMessageRecord, index: number }): void {
    this.currentTextMessageRecordIndex = data.index;
    this.updateSelectedTextMessage();
  }

  previousTextMessage(): void {
    if (this.currentTextMessageRecordIndex > 0) {
      const pageSize = this.outgoingSmsMessagesGridService.gridApi.paginationGetPageSize();
      const currentPage = this.outgoingSmsMessagesGridService.gridApi.paginationGetCurrentPage();
      const lastElementPrevPageIndex = pageSize * currentPage - 1;

      this.currentTextMessageRecordIndex--;
      if (this.currentTextMessageRecordIndex === lastElementPrevPageIndex) {
        this.outgoingSmsMessagesGridService.gridApi.paginationGoToPreviousPage();
        this.isDetailsLoading = true;
      }
      this.updateSelectedTextMessage();
    }
  }

  nextTextMessage(): void {
    if (this.currentTextMessageRecordIndex < this.totalRows()) {
      const pageSize = this.outgoingSmsMessagesGridService.gridApi.paginationGetPageSize();
      const currentPage = this.outgoingSmsMessagesGridService.gridApi.paginationGetCurrentPage();
      const firstElementNextPageIndex = (pageSize * currentPage + pageSize);

      this.currentTextMessageRecordIndex++;
      if (this.currentTextMessageRecordIndex === firstElementNextPageIndex) {
        this.outgoingSmsMessagesGridService.gridApi.paginationGoToNextPage();
        this.isDetailsLoading = true;
      }
      this.updateSelectedTextMessage();
    }
  }

  onCloseDetails(): void {
    this.currentTextMessageRecordIndex = -1;
    this.$selectedTextMessageRecord.set(null);
    this.reloadGrid();
  }

  private updateSelectedTextMessage(): void {
    const pageSize = this.outgoingSmsMessagesGridService.gridApi.paginationGetPageSize();
    const currentPage = this.outgoingSmsMessagesGridService.gridApi.paginationGetCurrentPage();

    this.currentTextMessageItemNumber = this.currentTextMessageRecordIndex + 1;

    let currentIndex = 0;
    if (this.currentTextMessageRecordIndex < pageSize) {
      currentIndex = this.currentTextMessageRecordIndex;
    } else {
      const countRows = (pageSize * currentPage);
      if (countRows) {
        currentIndex = this.currentTextMessageRecordIndex % (pageSize * currentPage);
      } else {
        currentIndex = 0;
      }
    }

    if (!this.isDetailsLoading)
      this.$selectedTextMessageRecord.set(this.textMessagesRows[currentIndex]);
  }

  // =====  Grid  =====

  readonly defaultPageSize = PageSizeService.defaultPageSize;
  readonly cacheBlockSize = 20;
  readonly textMessagesStatusFilterOptions = convertEnumToFilterOption(TextMessageStatus);


  dataSource = this.outgoingSmsMessagesGridService.dataSource;
  colDefs = this.outgoingSmsMessagesGridService.colDefs;
  totalRows = this.outgoingSmsMessagesGridService.totalRecords;

  gridOptions: GridOptions = {
    overlayNoRowsTemplate: '<span class="no-rows-overlay">No data available</span>',
    rowModelType: 'serverSide',
    pagination: true,
    paginationPageSize: 20,
    cacheBlockSize: 20,
    maxBlocksInCache: 1,
    getRowId: (params: GetRowIdParams<TextMessageRecord>) => params.data.textMessageId
  };

  onGridReady(gridApi: GridApi): void {
    this.outgoingSmsMessagesGridService.setGridApi(gridApi);
  }

  selectTextMessageRecord(textMessageRecords: TextMessageRecord[]): void {
    this.checkedTextMessageRecords = textMessageRecords;
  }

  onSearch(searchText = ''): void {
    this.outgoingSmsMessagesGridService.setDataSource(searchText);
  }

  setGridConfig(): void {
    const actionCell = {
      width: 60,
      minWidth: 60,
      sortable: false,
      cellRenderer: AgOutgoingMessagesActionsComponent,
      cellRendererParams: {
        resend: this.resendTextMessage.bind(this),
        delete: this.deleteTextMessage.bind(this),
        checkShowResend: (data: TextMessageRecord | undefined) => data?.status === TextMessageStatus.Failed,
        checkShowDelete: (data: TextMessageRecord | undefined) => data?.status !== TextMessageStatus.Created,
        checkDisabled: (data: TextMessageRecord | undefined) => data?.status === TextMessageStatus.Created,
      }
    };

    this.outgoingSmsMessagesGridService.setColDefsWithCellClickHandler(actionCell, this.cellClickHandler.bind(this));
  }

  reloadGrid(): void {
    this.outgoingSmsMessagesGridService.gridApi.refreshServerSide();
  }

  // =====  Export  =====

  readonly ExportTypeEnum = ExportTypeEnum;
  readonly excelExportParams = this.outgoingSmsMessagesExportService.exportParams() as ExcelExportParams;
  readonly excelStyles: ExcelStyle[] = excelReportTitleStyles;

  exportSelectedTextMessagesToXls(): void {
    this.exportTextMessagesList(true, ExportTypeEnum.EXCEL);
  }

  exportTextMessagesList(isBulkExport: boolean, exportType: ExportTypeEnum): void {
    if (this.modalOpened()) return;

    this.outgoingSmsMessagesExportService.exportTextMessagesList(
      this.outgoingSmsMessagesGridService.gridApi,
      this.colDefs(),
      this.checkedTextMessageRecords,
      this.totalRows(),
      isBulkExport,
      exportType
    );
  }
}

